import type { CreateCommonProductRequest, PatchCommonProductRequest } from './requests'
import type { CommonProductEdited, EditorState, FieldConflict, ProductFieldKey, ProductForm } from './types'

/**
 * Черновик правки общего товара — чистые функции без состояния приложения. Черновик живёт, пока открыто окно (срок `screen`):
 * человек видит значения (`base`, версия `version`), вводит свои (`mine`); если за это время товар поменял кто-то ещё,
 * поля сверяются тремя значениями «было / сейчас / ваше», а не затираются молча.
 */

export const FIELD_KEYS: readonly ProductFieldKey[] = [
  'name',
  'displayName',
  'sku',
  'unitCode',
  'unitName',
  'vatRate',
  'isWeighted',
  'paymentSubject',
  'paymentMethod',
  'barcodes',
]

export const FIELD_LABELS: Readonly<Record<ProductFieldKey, string>> = {
  name: 'Наименование',
  displayName: 'Отображаемое имя',
  sku: 'Артикул',
  unitCode: 'Код единицы (ОКЕИ)',
  unitName: 'Имя единицы',
  vatRate: 'Ставка НДС',
  isWeighted: 'Весовой признак',
  paymentSubject: 'Предмет расчёта',
  paymentMethod: 'Способ расчёта',
  barcodes: 'Штрихкоды',
}

export const EMPTY_FORM: ProductForm = {
  name: '',
  displayName: '',
  sku: '',
  unitCode: '',
  unitName: '',
  vatRate: '',
  isWeighted: '',
  paymentSubject: '',
  paymentMethod: '',
  barcodes: [],
}

/** Штрихкоды как их хранит каталог: без пробелов по краям, без пустых и повторов, порядок ввода сохраняется. */
export function normalizeBarcodes(codes: readonly string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const raw of codes) {
    const code = raw.trim()
    if (code === '' || seen.has(code)) continue
    seen.add(code)
    result.push(code)
  }
  return result
}

export function formOf(product: CommonProductEdited): ProductForm {
  return {
    name: product.name,
    displayName: product.displayNameOverride ?? '',
    sku: product.sku ?? '',
    unitCode: product.unitCode ?? '',
    unitName: product.unitName ?? '',
    vatRate: product.vatRate ?? '',
    isWeighted: product.isWeighted === null ? '' : product.isWeighted ? 'true' : 'false',
    paymentSubject: product.paymentSubject === null ? '' : String(product.paymentSubject),
    paymentMethod: product.paymentMethod === null ? '' : String(product.paymentMethod),
    barcodes: [...product.barcodes],
  }
}

/** Значение поля для сравнения и показа: штрихкоды — строкой через запятую, по возрастанию (порядок не важен каталогу). */
export function fieldText(form: ProductForm, field: ProductFieldKey): string {
  if (field === 'barcodes') return [...normalizeBarcodes(form.barcodes)].sort().join(', ')
  return form[field].trim()
}

const same = (a: ProductForm, b: ProductForm, field: ProductFieldKey): boolean => fieldText(a, field) === fieldText(b, field)

export function changedFields(base: ProductForm, mine: ProductForm): ProductFieldKey[] {
  return FIELD_KEYS.filter((field) => !same(base, mine, field))
}

export function isDirty(state: EditorState): boolean {
  return changedFields(state.base, state.mine).length > 0
}

/** Почему сохранить нельзя: `null` — можно. Остальное проверит сервер, и его текст будет показан дословно. */
export function blockerOf(form: ProductForm): string | null {
  return form.name.trim() === '' ? 'Введите наименование — без него товар не сохраняется.' : null
}

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim())

/** Весовой признак в теле запроса: пустое — `null` («не знаю»). */
function weightedOf(form: ProductForm): boolean | null {
  const value = orNull(form.isWeighted)
  return value === null ? null : value === 'true'
}

/** Предмет и способ расчёта в теле запроса — число; пустое — `null` («не знаю»). */
function cashCodeOf(form: ProductForm, field: 'paymentSubject' | 'paymentMethod'): number | null {
  const value = orNull(form[field])
  return value === null ? null : Number(value)
}

/** Тело создания: название обязательно, остальное — только заданное. */
export function createBodyOf(form: ProductForm, operationId: string): CreateCommonProductRequest {
  return {
    operationId,
    name: form.name.trim(),
    sku: orNull(form.sku),
    unitCode: orNull(form.unitCode),
    unitName: orNull(form.unitName),
    vatRate: orNull(form.vatRate),
    isWeighted: weightedOf(form),
    paymentSubject: cashCodeOf(form, 'paymentSubject'),
    paymentMethod: cashCodeOf(form, 'paymentMethod'),
    barcodes: normalizeBarcodes(form.barcodes),
  }
}

/** Тело правки: только изменённые поля; пустое необязательное поле уходит как `null` (сервер очищает), отсутствующее — не трогается. */
export function patchBodyOf(base: ProductForm, form: ProductForm, operationId: string, expectedVersion: number): PatchCommonProductRequest {
  const body: PatchCommonProductRequest = { operationId, expectedVersion }
  for (const field of changedFields(base, form)) {
    if (field === 'name') body.name = form.name.trim()
    else if (field === 'displayName') body.displayNameOverride = orNull(form.displayName)
    else if (field === 'sku') body.sku = orNull(form.sku)
    else if (field === 'unitCode') body.unitCode = orNull(form.unitCode)
    else if (field === 'barcodes') body.barcodes = normalizeBarcodes(form.barcodes)
    else if (field === 'unitName') body.unitName = orNull(form.unitName)
    else if (field === 'vatRate') body.vatRate = orNull(form.vatRate)
    else if (field === 'isWeighted') body.isWeighted = weightedOf(form)
    else body[field] = cashCodeOf(form, field)
  }
  return body
}

export function initialState(product: CommonProductEdited | null): EditorState {
  return product === null
    ? { base: EMPTY_FORM, version: 0, mine: EMPTY_FORM, conflicts: [] }
    : { base: formOf(product), version: product.version, mine: formOf(product), conflicts: [] }
}

export function fieldChanged(state: EditorState, field: Exclude<ProductFieldKey, 'barcodes'>, value: string): EditorState {
  return { ...state, mine: { ...state.mine, [field]: value } }
}

/** Добавить штрихкод; повтор не добавляется. */
export function barcodeAdded(state: EditorState, code: string): EditorState {
  return { ...state, mine: { ...state.mine, barcodes: normalizeBarcodes([...state.mine.barcodes, code]) } }
}

export function barcodeRemoved(state: EditorState, code: string): EditorState {
  return { ...state, mine: { ...state.mine, barcodes: state.mine.barcodes.filter((existing) => existing !== code) } }
}

/** «Отменить изменения»: ваши значения возвращаются к тем, что в каталоге; разногласия снимаются. */
export function discarded(state: EditorState): EditorState {
  return { ...state, mine: state.base, conflicts: [] }
}

const withField = (form: ProductForm, from: ProductForm, field: ProductFieldKey): ProductForm =>
  field === 'barcodes' ? { ...form, barcodes: [...from.barcodes] } : { ...form, [field]: from[field] }

/**
 * Пришла свежая версия товара (после перечитывания, после своего сохранения или после чужой правки).
 * Поле, которого вы не трогали, берёт значение каталога. Поле, которое поменяли только вы или вы и каталог одинаково, остаётся вашим.
 * Поле, которое поменяли и вы, и кто-то другой по-разному, остаётся вашим и попадает в разногласия — решает человек.
 */
export function withServer(state: EditorState, product: CommonProductEdited): EditorState {
  const server = formOf(product)
  if (product.version === state.version && FIELD_KEYS.every((field) => same(server, state.base, field))) return state
  let mine = state.mine
  const found = new Map<ProductFieldKey, FieldConflict>()
  for (const field of FIELD_KEYS) {
    const wasMine = !same(state.mine, state.base, field)
    const serverMoved = !same(server, state.base, field)
    if (!wasMine) mine = withField(mine, server, field)
    else if (serverMoved && !same(state.mine, server, field)) {
      found.set(field, { field, was: fieldText(state.base, field), server: fieldText(server, field), mine: fieldText(state.mine, field) })
    }
  }
  // Прежние разногласия остаются, пока человек их не решил, — но не там, где ваше значение уже совпало с каталогом.
  for (const old of state.conflicts) {
    if (!found.has(old.field) && !same(mine, server, old.field)) found.set(old.field, { ...old, server: fieldText(server, old.field) })
  }
  return { base: server, version: product.version, mine, conflicts: FIELD_KEYS.flatMap((field) => found.get(field) ?? []) }
}

/** «Взять из каталога»: поле принимает значение каталога, разногласие снимается. */
export function serverTaken(state: EditorState, field: ProductFieldKey): EditorState {
  return {
    ...state,
    mine: withField(state.mine, state.base, field),
    conflicts: state.conflicts.filter((conflict) => conflict.field !== field),
  }
}

/** «Оставить моё»: разногласие снимается, ваше значение уйдёт от свежей версии. */
export function mineKept(state: EditorState, field: ProductFieldKey): EditorState {
  return { ...state, conflicts: state.conflicts.filter((conflict) => conflict.field !== field) }
}
